import type { PublicPetitionWizardDraft } from '../../../types/publicPetitionCaseRequest';
import {
  contactNumberError,
  exceedsMax,
  isTodayOrFutureIsoDate,
  maxCharLimitExceededError,
} from '../../../utils/caseFieldValidation';
import type { RequiredErrors, RequiredFieldSpec } from '../../../utils/requiredValidation';
import { validateRequired } from '../../../utils/requiredValidation';
import { validationDateFutureOnly, validationDocReasonRequired } from '../../../utils/validationI18n';
import {
  draftRequiresSubFileCategory,
  type SubFileCategoryValidationContext,
} from '../../../utils/subFileCategoryRules';
import type { PublicPetitionWizardStep } from './publicPetitionConstants';
import { draftRequiresOtherDeliveryText } from '../../../utils/deliveryMethod';
import { isPublicPetitionUrgencyJustificationRequired, numberOfRespondentsError } from './publicPetitionHelpers';

export type PublicPetitionStepValidationOptions = SubFileCategoryValidationContext & {
  urgencyLevels?: ReadonlyArray<{ id: string; name: string }>;
};

const MAX_DESC = 2000;
const MAX_REF = 255;

export const MAX_DOC_SKIP_REASON = 255;

export function validatePublicPetitionStep(
  step: PublicPetitionWizardStep,
  draft: PublicPetitionWizardDraft,
  options?: PublicPetitionStepValidationOptions,
): { valid: boolean; errors: RequiredErrors; specs: RequiredFieldSpec[] } {
  const specs: RequiredFieldSpec[] = [];
  const extra: RequiredErrors = {};

  if (step >= 1) {
    specs.push(
      { key: 'fileCategoryId', label: 'File Category', value: draft.fileCategoryId, elementId: 'pp-fileCategory' },
      { key: 'description', label: 'Description', value: draft.description, elementId: 'pp-description' },
      { key: 'caseMatter', label: 'Case Matter', value: draft.caseMatter, elementId: 'pp-caseMatter' },
      { key: 'provinceId', label: 'Province', value: draft.provinceId, elementId: 'pp-province' },
      { key: 'yourReferenceNumber', label: 'Your Reference Number (External)', value: draft.yourReferenceNumber, elementId: 'pp-yourRef' },
      { key: 'hasConnectedMatters', label: 'Connected matters', value: draft.hasConnectedMatters, elementId: 'pp-connected' },
    );

    if (draftRequiresSubFileCategory(draft, options)) {
      specs.push({
        key: 'subFileCategoryId',
        label: 'Sub File Category',
        value: draft.subFileCategoryId,
        elementId: 'pp-subFileCategory',
      });
    }

    if (draft.hasConnectedMatters === 'yes') {
      specs.push({
        key: 'connectedCaseNumber',
        label: 'Connected Case Number',
        value: draft.connectedCaseNumber,
        elementId: 'pp-connectedCase',
      });
      if (draft.connectedAgdFileRefAvailable) {
        specs.push({
          key: 'connectedAgdFileRef',
          label: 'Connected AGD File Reference Number',
          value: draft.connectedAgdFileRef,
          elementId: 'pp-connectedAgd',
        });
      }
    }

    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.yourReferenceNumber, MAX_REF)) extra.yourReferenceNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.otherExternalReferences, MAX_REF)) extra.otherExternalReferences = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_REF)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_REF)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_REF)) extra.connectedAgdFileRef = maxCharLimitExceededError();
  }

  if (step >= 2) {
    specs.push(
      { key: 'petitionerName', label: 'Petitioner Name', value: draft.petitionerName, elementId: 'pp-petitionerName' },
      { key: 'petitionerAddress', label: 'Petitioner Address', value: draft.petitionerAddress, elementId: 'pp-petitionerAddress' },
      { key: 'respondentNames', label: 'Respondent Name(s)', value: draft.respondentNames, elementId: 'pp-respondentNames' },
    );

    if (exceedsMax(draft.petitionerName, MAX_REF)) extra.petitionerName = maxCharLimitExceededError();
    if (exceedsMax(draft.nicOrIdentificationNumber, MAX_REF)) extra.nicOrIdentificationNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.petitionerAddress, MAX_DESC)) extra.petitionerAddress = maxCharLimitExceededError();
    const petContactErr = contactNumberError(draft.petitionerContactNumber);
    if (petContactErr) extra.petitionerContactNumber = petContactErr;
    if (exceedsMax(draft.petitionerEmail, MAX_REF)) extra.petitionerEmail = maxCharLimitExceededError();
    if (draft.representedByAttorney) {
      if (exceedsMax(draft.attorneyName, MAX_REF)) extra.attorneyName = maxCharLimitExceededError();
      const attContactErr = contactNumberError(draft.attorneyContactNumber);
      if (attContactErr) extra.attorneyContactNumber = attContactErr;
    }
    if (exceedsMax(draft.respondentNames, MAX_DESC)) extra.respondentNames = maxCharLimitExceededError();
    if (exceedsMax(draft.respondentAddress, MAX_DESC)) extra.respondentAddress = maxCharLimitExceededError();
    const respondentsErr = numberOfRespondentsError(draft.numberOfRespondents);
    if (respondentsErr) extra.numberOfRespondents = respondentsErr;
  }

  if (step >= 3) {
    specs.push({ key: 'urgencyLevelId', label: 'Urgency Level', value: draft.urgencyLevelId, elementId: 'pp-urgency' });
    if (isPublicPetitionUrgencyJustificationRequired(draft, options?.urgencyLevels)) {
      specs.push({
        key: 'urgencyJustification',
        label: 'Urgency Justification',
        value: draft.urgencyJustification,
        elementId: 'pp-urgencyJustification',
      });
    }
    if (exceedsMax(draft.urgencyJustification, MAX_DESC)) extra.urgencyJustification = maxCharLimitExceededError();
    if (draft.deadline && !isTodayOrFutureIsoDate(draft.deadline)) {
      extra.deadline = validationDateFutureOnly();
    }
  }

  if (step >= 4) {
    specs.push({ key: 'deliveryMethodIds', label: 'Delivery Method', value: draft.deliveryMethodIds, elementId: 'pp-delivery' });
    if (draftRequiresOtherDeliveryText(draft)) {
      specs.push({
        key: 'deliveryOther',
        label: 'Other Delivery Method',
        value: draft.deliveryOther,
        elementId: 'pp-deliveryOther',
      });
    }
    if (exceedsMax(draft.deliveryOther, MAX_REF)) extra.deliveryOther = maxCharLimitExceededError();
  }

  if (step >= 5) {
    Object.assign(extra, validateStep5Documents(draft));
  }

  const reqErrs = validateRequired(specs);
  const errors = { ...reqErrs, ...extra };
  return { valid: Object.keys(errors).length === 0, errors, specs };
}

export function validateStep5Documents(draft: PublicPetitionWizardDraft): RequiredErrors {
  const errors: RequiredErrors = {};
  draft.documents.forEach((doc) => {
    if (doc.fileName) return;
    if (doc.mandatory && !doc.skipReason.trim()) {
      errors[`doc-reason-${doc.id}`] = validationDocReasonRequired();
      return;
    }
    if (exceedsMax(doc.skipReason, MAX_DOC_SKIP_REASON)) {
      errors[`doc-reason-${doc.id}`] = maxCharLimitExceededError();
    }
  });
  return errors;
}
